'use client';

import { Calendar, Clock, Map, Search, Tag } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DateRangeFilter, MultiSelectFilterCompact } from '@ui/components/features/search/filterInputs';
import { type CategoryItem } from '@ui/components/features/search/searchMobileHeader';
import { Popover, PopoverAnchor, PopoverContent } from '@ui/components/ui/popover';
import { cn } from '@ui/lib/utils';
import { KeywordSuggestionsList } from '@website/components/KeywordSuggestionsDropdown';
import { isFeatureEnabled } from '@website/config/featureFlags';
import { SEARCH_PARAMS } from '@website/constants/searchParams';
import { useIsMobile } from '@website/hooks/useIsMobile';
import { useSearchParam } from '@website/hooks/useSearchParam';
import { useSuggestions } from '@website/hooks/useSuggestions';

import { LocationFilter } from './LocationFilter';

interface SearchFiltersProps {
  className?: string;
  categories?: CategoryItem[];
  setLocation: (location: { latitude: number; longitude: number; name: string }) => void;
  onLocationPickerOpenChange?: (open: boolean) => void;
}

export function SearchFilters({ className, categories, setLocation, onLocationPickerOpenChange }: SearchFiltersProps) {
  const isMobile = useIsMobile();
  const t = useTranslations();
  const tCommon = useTranslations('Common');
  const locale = useLocale();

  const keywordSearchEnabled = isFeatureEnabled('KEYWORD_SEARCH');
  const [queryValues, setQueryValues] = useSearchParam(SEARCH_PARAMS.QUERY);
  const currentQuery = keywordSearchEnabled ? queryValues[0] || '' : '';
  const [localQuery, setLocalQuery] = useState(currentQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(currentQuery);
  const [showKeywordDropdown, setShowKeywordDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setLocalQuery(currentQuery);
  }, [currentQuery]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(localQuery), 200);
    return () => clearTimeout(id);
  }, [localQuery]);

  const { data: keywordSuggestions = [], isFetching: isFetchingSuggestions } = useSuggestions(debouncedQuery, locale);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleQueryChange = useCallback(
    (value: string) => {
      setLocalQuery(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setQueryValues(value.trim() ? [value.trim()] : []);
      }, 300);
    },
    [setQueryValues]
  );

  const handleSuggestionSelect = useCallback(
    (item: { name: string }) => {
      handleQueryChange(item.name);
      setShowKeywordDropdown(false);
    },
    [handleQueryChange]
  );

  const [displayName] = useSearchParam(SEARCH_PARAMS.DISPLAY_NAME);
  const locationLabel = displayName[0] || t('SearchPage.chooseLocation');

  const [categoryValues, setCategoryValues] = useSearchParam(SEARCH_PARAMS.CATEGORY_ID);
  const [dateValues, setDateValues] = useSearchParam(SEARCH_PARAMS.DATE);
  const [, setDateFrom] = useSearchParam(SEARCH_PARAMS.DATE_FROM);
  const [, setDateTo] = useSearchParam(SEARCH_PARAMS.DATE_TO);
  const [sessionTimeValues, setSessionTimeValues] = useSearchParam(SEARCH_PARAMS.SESSION_TIME);
  const [venueValues, setVenueValues] = useSearchParam(SEARCH_PARAMS.IS_AT_HOME);

  const handleDateChange = useCallback(
    (values: string[], customRange?: { from?: Date; to?: Date }) => {
      setDateValues(values);
      if (values.includes('custom') && customRange?.from && customRange?.to) {
        setDateFrom([customRange.from.toISOString()]);
        setDateTo([customRange.to.toISOString()]);
      } else {
        setDateFrom([]);
        setDateTo([]);
      }
    },
    [setDateValues, setDateFrom, setDateTo]
  );

  return (
    <div
      className={cn(
        // On mobile (< sm): the row fills the full width; the location button stretches
        // while the other (icon-only) filters keep their natural compact width.
        // On sm+: restore the original horizontal-scroll layout with natural widths.
        'flex flex-row items-center gap-2 sm:gap-3 sm:overflow-x-auto',
        'scrollbar-width-none [&::-webkit-scrollbar]:hidden [&::-ms-overflow-style:none]',
        className
      )}
    >
      <LocationFilter
        isMobile={isMobile}
        locationLabel={locationLabel}
        isActive={displayName.length > 0 ? true : undefined}
        setLocation={setLocation}
        onOpenChange={onLocationPickerOpenChange}
        className="flex-1 min-w-0 sm:flex-none"
      />

      {keywordSearchEnabled && (
        <Popover open={showKeywordDropdown} onOpenChange={setShowKeywordDropdown}>
          <PopoverAnchor asChild>
            <div className="flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 min-w-0 sm:min-w-[180px]">
              <Search className="w-4 h-4 text-neutral-500 flex-shrink-0" />
              <input
                type="text"
                value={localQuery}
                onChange={(e) => {
                  handleQueryChange(e.target.value);
                  setShowKeywordDropdown(true);
                }}
                onFocus={() => setShowKeywordDropdown(true)}
                placeholder={t('SearchPage.searchPlaceholder')}
                className="bg-transparent outline-none text-base md:text-sm text-neutral-700 w-full min-w-0 placeholder:text-neutral-500"
              />
            </div>
          </PopoverAnchor>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px] max-h-[280px] overflow-y-auto"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <KeywordSuggestionsList
              query={localQuery}
              suggestions={keywordSuggestions}
              isLoading={isFetchingSuggestions}
              onSelect={handleSuggestionSelect}
            />
          </PopoverContent>
        </Popover>
      )}

      {categories && categories.length > 0 && (
        <MultiSelectFilterCompact
          key={SEARCH_PARAMS.CATEGORY_ID}
          label={t('SearchPage.category')}
          icon={<Tag className="w-5 h-5 text-neutral-900" />}
          onChange={setCategoryValues}
          value={categoryValues}
          options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
          showClear={true}
          clearText={t('SearchPage.clear')}
          hideLabelOnMobile
          className="flex-shrink-0"
        />
      )}

      <DateRangeFilter
        label={t('SearchPage.date')}
        icon={<Calendar className="w-5 h-5 text-neutral-900" />}
        options={[
          { value: 'today', label: t('SearchPage.today') },
          { value: 'tomorrow', label: t('SearchPage.tomorrow') },
          { value: 'this-week', label: t('SearchPage.thisWeek') },
          { value: 'this-month', label: t('SearchPage.thisMonth') },
          { value: 'next-30-days', label: t('SearchPage.next30Days') },
          { value: 'custom', label: t('SearchPage.customDuration') }
        ]}
        value={dateValues}
        onChange={handleDateChange}
        showClear={true}
        clearText={t('SearchPage.clear')}
        customDateLabel={t('SearchPage.customDuration')}
        locale={locale}
        translations={{
          cancel: tCommon('cancel'),
          apply: tCommon('apply'),
          maxDaysWarning: t('SearchPage.maxDaysWarning', { maxDays: '90' })
        }}
        hideLabelOnMobile
        className="flex-shrink-0"
      />

      <MultiSelectFilterCompact
        label={t('SearchPage.sessionTime')}
        icon={<Clock className="w-5 h-5 text-neutral-900" />}
        onChange={setSessionTimeValues}
        value={sessionTimeValues}
        options={[
          { value: 'morning', label: t('SearchPage.morning') },
          { value: 'afternoon', label: t('SearchPage.afternoon') },
          { value: 'evening', label: t('SearchPage.evening') }
        ]}
        showClear={true}
        clearText={t('SearchPage.clear')}
        hideLabelOnMobile
        className="flex-shrink-0"
      />

      <MultiSelectFilterCompact
        label={t('SearchPage.activityVenue')}
        icon={<Map className="w-5 h-5 text-neutral-900" />}
        onChange={setVenueValues}
        value={venueValues}
        options={[
          { value: 'true', label: t('SearchPage.filterAtHome') },
          { value: 'false', label: t('SearchPage.filterAtVenue') }
        ]}
        showClear={true}
        clearText={t('SearchPage.clear')}
        hideLabelOnMobile
        className="flex-shrink-0"
      />
    </div>
  );
}
